import { useEffect, useRef, useState } from "react";

const isBrowser = typeof window !== "undefined";

export function useLocalStorageState<T>(key: string, defaultValue: T) {
  // Initialize state synchronously from localStorage if in browser
  const getInitialState = (): T => {
    if (!isBrowser) {
      return defaultValue;
    }

    try {
      const item = localStorage.getItem(key);
      if (item !== null) {
        return JSON.parse(item);
      }
    } catch (error) {
      console.error(`Error parsing localStorage key "${key}":`, error);
    }
    return defaultValue;
  };

  const [state, setState] = useState<T>(getInitialState);
  const [isInitialized, setIsInitialized] = useState(false);
  const currentKeyRef = useRef<string | null>(null);

  // Handle key changes and set initialization flag
  useEffect(() => {
    if (!isBrowser) {
      return;
    }

    // Only read from localStorage if the key has actually changed
    if (currentKeyRef.current !== key) {
      currentKeyRef.current = key;
      setIsInitialized(false);

      try {
        const item = localStorage.getItem(key);
        if (item !== null) {
          setState(JSON.parse(item));
        } else {
          setState(defaultValue);
        }
      } catch (error) {
        console.error(`Error parsing localStorage key "${key}":`, error);
        setState(defaultValue);
      }

      setIsInitialized(true);
    } else if (!isInitialized) {
      // If key hasn't changed but we're not initialized, mark as initialized
      setIsInitialized(true);
    }
  }, [key, defaultValue, isInitialized]);

  // Update localStorage when state changes (but only after initial load)
  useEffect(() => {
    if (!isBrowser || !isInitialized) {
      return;
    }

    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  }, [key, state, isInitialized]);

  return [state, setState] as const;
}
